import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Box, Typography, Drawer, IconButton, Button } from "@mui/material";
import MemberSecretsComponent from "components/member/MemberSecrets";
import Loading from "components/common/Loading";
import useIsMobile from "components/hooks/useIsMobile";
import Secret from "components/secrets/Secret";
import FlexPage from "components/layout/FlexPage";
import Breadcrumbs from "components/common/Breadcrumbs";
import { ReactComponent as CloseIcon } from "../icons/Close.svg";
import { useListMemberSecrets } from "services/secrets";

export default () => {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const { data, request } = useListMemberSecrets();
  const [showSecondPanel, setShowSecondPanel] = useState(false);
  const [selectedSecret, setSelectedSecret] = useState("");

  useEffect(() => {
    request();
  }, []);

  const handleClickSecret = (secretRef: string) => {
    if (isMobile) {
      navigate(`/secret/${encodeURIComponent(secretRef)}`);
    } else {
      setSelectedSecret(secretRef);
      setShowSecondPanel(true);
    }
  };

  const closeSecondPanel = (doRefresh?: boolean) => {
    setSelectedSecret("");
    setShowSecondPanel(false);
    if (doRefresh) {
      request();
    }
  };

  const onSaveHandler = () => closeSecondPanel(true);
  const onCancelHandler = () => closeSecondPanel();

  // Only on initial load.
  if (!data) {
    return <Loading />;
  }

  const { secrets, creators } = data;
  const sortedSecrets = secrets.sort((a, b) => {
    if (a.valueType < b.valueType) return -1;
    if (a.valueType > b.valueType) return 1;

    return Number(a.creationTimeSec - b.creationTimeSec);
  });

  return (
    <FlexPage leftAligned sx={{ width: "100%" }}>
      <Box sx={{ display: "flex", justifyContent: "space-between" }}>
        <Box>
          <Breadcrumbs
            breadcrumbs={[{ name: "Home", link: "/" }, { name: "Secrets" }]}
          />
          <Typography variant="display" sx={{ marginBottom: "24px" }}>
            Secrets
          </Typography>
        </Box>
        <Box>
          <Button
            variant="outlined"
            onClick={() => {
              handleClickSecret("");
            }}
          >
            New secret
          </Button>
        </Box>
      </Box>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: "32px",
          width: "100%",
          maxWidth: "800px",
          marginTop: "24px",
        }}
      >
        <Box sx={{}}>
          <Typography
            variant="bodySmall"
            sx={{
              color: "#6B6E7B",
            }}
          >
            This is a secure, encrypted space to store sensitive data and
            passwords.
          </Typography>
        </Box>
        <MemberSecretsComponent
          secrets={sortedSecrets}
          creators={creators || []}
          handleSecretClick={handleClickSecret}
        />
      </Box>

      <Drawer
        anchor="right"
        open={showSecondPanel}
        onClose={() => closeSecondPanel()}
        sx={{
          "& .MuiDrawer-paper": {
            width: isMobile ? "100%" : "600px",
            boxSizing: "border-box",
          },
        }}
      >
        <Box
          sx={{
            padding: "24px",
            height: "100%",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: "24px",
            }}
          >
            <Typography variant="h2">
              {selectedSecret ? "Edit Secret" : "New Secret"}
            </Typography>
            <IconButton onClick={() => closeSecondPanel()}>
              <CloseIcon />
            </IconButton>
          </Box>
          <Box sx={{ flex: 1 }}>
            <Secret
              secretRef={selectedSecret}
              onSaveHandler={onSaveHandler}
              onCancelHandler={onCancelHandler}
            />
          </Box>
        </Box>
      </Drawer>
    </FlexPage>
  );
};
